import { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import COLORS from '../../../../constants/colors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SnackbarAlert from '../Reusbale/SnackbarAlert';
import baseURL from '../config';
import Footer from '../../user/Reusbale/Footer';
import { toast } from 'react-toastify';

const ResetPasswordForm = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Get email and token from URL parameters
    const email = searchParams.get('email');
    const token = searchParams.get('token');

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
    const toggleConfirmPasswordVisibility = () => setShowConfirmPassword((prev) => !prev);

    const validatePassword = (password) => {
        const regex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{6,}$/;
        return regex.test(password);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!email || !token) {
            
            toast.error('Invalid or expired reset link!');
            setLoading(false);
            return;
        }

        if (!password || !confirmPassword) {
            toast.error('All fields are required!');

            setLoading(false);
            return;
        }

        if (!validatePassword(password)) {
            
            toast.error('Password must be at least 6 characters, include 1 number & 1 symbol!');

            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match!');

            setLoading(false);
            return;
        }

        try {
            // Proceed with password reset
            const response = await fetch(`${baseURL}/auth/admin/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, token, password }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Error resetting password');
            }

            toast.success('Password reset successful! Redirecting...');


            // Redirect to login after 3 seconds
            setTimeout(() => navigate('/admin/login'), 3000);
        } catch (error) {
            toast.error(error.message);

        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Centering Box */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ py: 3, px: 3, maxWidth: 400, width: '100%', textAlign: 'center' }}>
                    {/* Header */}
                    <Typography variant="h4" fontWeight="bold" color={COLORS.primary} mb={2}>
                        Set New Password
                    </Typography>
                    <Typography variant="body1" color={COLORS.text} mb={4}>
                        Enter a new password to reset your account.
                    </Typography>

                    {/* Reset Password Form */}
                    <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
                    <TextField
    label="Email"
    type="email"
    variant="outlined"
    fullWidth
    value={email || ''}
    disabled
    sx={{ mb: 2, borderRadius: '10px', '& fieldset': { borderRadius: '10px' } }} // Added borderRadius
/>

<TextField
    name="password"
    label="New Password"
    type={showPassword ? 'text' : 'password'}
    variant="outlined"
    fullWidth
    value={password}
    onChange={(e) => setPassword(e.target.value)}
    sx={{ mb: 2, borderRadius: '10px', '& fieldset': { borderRadius: '10px' } }} // Added borderRadius
    InputProps={{
        startAdornment: (
            <InputAdornment position="start">
                <FontAwesomeIcon icon={faLock} style={{ color: COLORS.primary }} />
            </InputAdornment>
        ),
        endAdornment: (
            <InputAdornment position="end">
                <IconButton onClick={togglePasswordVisibility} edge="end">
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </IconButton>
            </InputAdornment>
        ),
    }}
/>

<TextField
    name="confirmPassword"
    label="Confirm Password"
    type={showConfirmPassword ? 'text' : 'password'}
    variant="outlined"
    fullWidth
    value={confirmPassword}
    onChange={(e) => setConfirmPassword(e.target.value)}
    sx={{ mb: 2, borderRadius: '10px', '& fieldset': { borderRadius: '10px' } }} // Added borderRadius
    InputProps={{
        startAdornment: (
            <InputAdornment position="start">
                <FontAwesomeIcon icon={faLock} style={{ color: COLORS.primary }} />
            </InputAdornment>
        ),
        endAdornment: (
            <InputAdornment position="end">
                <IconButton onClick={toggleConfirmPasswordVisibility} edge="end">
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} 
                    style={{ fontSize: '15px' }} // smaller icon
                    />
                </IconButton>
            </InputAdornment>
        ),
    }}
/>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{
                                backgroundColor: COLORS.primary,
                                color: COLORS.white,
                                borderRadius: '10px',
                                py: 1.2,
                                fontSize: '1rem',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: COLORS.secondary },
                                display: 'flex',
                                alignItems: 'center',
                                boxShadow:0,
                                justifyContent: 'center',
                                gap: '10px',
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={22} sx={{ color: COLORS.white }} />
                                    <span>Resetting...</span>
                                </>
                            ) : 'Reset Password'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Snackbar for Messages */}
            <SnackbarAlert open={snackbar.open} message={snackbar.message} severity={snackbar.severity} handleClose={handleCloseSnackbar} />
            <Footer />
        </Box>
    );
};

export default ResetPasswordForm;
